// WHAT: Tab component for OAuth2 token management and status display
// WHY:  Provides interface for viewing and managing Withings OAuth2 tokens
// DOMAIN: Health → Withings
// DEPENDENCIES: WithingsTokenService, TimeRemaining, StatusCard components
// RISKS:
//   - NOTE: Token expiration calculation may have timezone issues
//   - NOTE: Status determination (10 days threshold) is hardcoded - may not match user needs
//   - NOTE: Error handling is silent - may not provide user feedback

import type { WithingsTokenData } from "../../types/domain";

import { Key, RefreshCw, XCircle, AlertCircle, CheckCircle, Clock } from "lucide-react";
import React, { useState, useCallback, useEffect } from "react";

import { Button, Card, PageLoader, StatusCard, TimeRemaining } from "@/components/ui";

import { WithingsTokenService } from "../../services/withings-token-service";

interface TokenTabProperties {
  isLoading?: boolean;
}

/**
 * Formats timestamp to readable date string.
 *
 * @param timestamp - Unix timestamp in milliseconds
 *
 * @returns {string} Formatted date string
 *
 */
const formatDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString("en-US", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    second: "2-digit",
    year: "numeric",
  });
};

/**
 * WHAT:
 *   Tab component that displays OAuth2 token status, validity, expiration
 *   information, and management options for Withings integration.
 *
 * WHY:
 *   Enables users to monitor and manage their Withings OAuth2 tokens with
 *   clear status indicators.
 *
 * PROPS:
 *   - isLoading?: boolean — Loading state
 *
 * BEHAVIOR:
 *   - Loads token data on mount
 *   - Displays access and refresh token status
 *   - Shows expiration times and remaining time
 *   - Provides refresh and clear token actions
 *   - Handles loading and empty states
 *
 * RISKS:
 *   - NOTE: Token expiration calculation may have timezone issues
 *   - NOTE: Status determination (10 days threshold) is hardcoded - may not match user needs
 *   - NOTE: Error handling is silent - may not provide user feedback
 *
 * @param root0
 *
 * @param root0.isLoading
 *
 * @returns {JSX.Element} Token tab component with OAuth2 token status and management
 *
 */
export const TokenTab: React.FC<TokenTabProperties> = ({ isLoading = false }) => {
  const [tokenData, setTokenData] = useState<undefined | WithingsTokenData>();
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);

  const loadTokenData = useCallback(async () => {
    try {
      setIsLoadingTokens(true);

      // NOTE: Load token data
      const tokens = await WithingsTokenService.getStoredTokens();
      setTokenData(tokens);
    } catch {
      // NOTE: Failed to load token data - silently fail
    } finally {
      setIsLoadingTokens(false);
    }
  }, []);

  useEffect(() => {
    void loadTokenData();
  }, [loadTokenData]);

  /**
   * WHAT:
   *   Determines token status based on expiration time.
   *
   * WHY:
   *   Provides visual status indicators for token validity.
   *
   * BEHAVIOR:
   *   - Returns expired if time left <= 0
   *   - Returns expiring soon if < 10 days left
   *   - Returns active otherwise
   *
   * @param expiresAt - Expiration timestamp
   *
   * @returns {object} Token status object with icon, label, and variant
   *
   */
  const getTokenStatus = (expiresAt: number) => {
    const now = Date.now();
    const timeLeft = expiresAt - now;
    const daysLeft = timeLeft / (1000 * 60 * 60 * 24);

    if (timeLeft <= 0) {
      return {
        icon: <XCircle className="h-4 w-4" />,
        label: "Expired",
        variant: "destructive" as const,
      };
    }

    if (daysLeft < 10) {
      return {
        icon: <AlertCircle className="h-4 w-4" />,
        label: "Expiring Soon",
        variant: "warning" as const,
      };
    }

    return {
      icon: <CheckCircle className="h-4 w-4" />,
      label: "Active",
      variant: "success" as const,
    };
  };

  if (isLoading || isLoadingTokens) {
    return <PageLoader message="Loading token data..." size="lg" withBackdrop={false} />;
  }

  if (!tokenData) {
    return (
      <div className="space-y-6">
        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <div className="p-8 text-center">
            <Key className="h-16 w-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Tokens Found</h3>
            <p className="text-slate-300">Please authenticate with Withings first</p>
          </div>
        </Card>
      </div>
    );
  }

  const accessTokenStatus = getTokenStatus(tokenData.expiresAt);
  const refreshTokenStatus = getTokenStatus(tokenData.refreshExpiresAt);

  return (
    <div className="space-y-6">
      {/* Token Status Overview */}
      <Card className="bg-slate-800/50 border-slate-700 text-white">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white flex items-center">
              <Key className="h-6 w-6 mr-2 text-green-500" />
              Token Status
            </h3>
            <TimeRemaining expiresAt={tokenData.expiresAt} showIcon showStatus size="md" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Access Token Card */}
            <StatusCard
              items={[
                {
                  label: "Status",
                  value: "Stored Securely",
                },
                {
                  label: "Type",
                  value: tokenData.tokenType,
                },
                {
                  label: "Expires",
                  value: formatDate(tokenData.expiresAt),
                },
                {
                  icon: <Clock className="h-3 w-3" />,
                  label: "Remaining",
                  value: (
                    <TimeRemaining
                      expiresAt={tokenData.expiresAt}
                      showIcon={false}
                      showStatus={false}
                      size="sm"
                    />
                  ),
                },
              ]}
              status={{
                icon: accessTokenStatus.icon,
                label: accessTokenStatus.label,
                variant: accessTokenStatus.variant,
              }}
              title="Access Token"
            />

            {/* Refresh Token Card */}
            <StatusCard
              items={[
                {
                  label: "Status",
                  value: "Stored Securely",
                },
                {
                  label: "Expires",
                  value: formatDate(tokenData.refreshExpiresAt),
                },
                {
                  icon: <Clock className="h-3 w-3" />,
                  label: "Remaining",
                  value: (
                    <TimeRemaining
                      expiresAt={tokenData.refreshExpiresAt}
                      showIcon={false}
                      showStatus={false}
                      size="sm"
                    />
                  ),
                },
              ]}
              status={{
                icon: refreshTokenStatus.icon,
                label: refreshTokenStatus.label,
                variant: refreshTokenStatus.variant,
              }}
              title="Refresh Token"
            />
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-4">
            <Button
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={() => {
                void loadTokenData();
              }}
              variant="outline"
            >
              Refresh
            </Button>
            <Button
              icon={<Key className="h-4 w-4" />}
              onClick={() => {
                void (async () => {
                  await WithingsTokenService.clearTokens();
                  await loadTokenData();
                })();
              }}
              variant="danger"
            >
              Clear Tokens
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
